import { Injectable, inject } from '@angular/core';
import { TierUploadQuota } from './../../shared/interfaces/tier-upload-quota';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ConsentService } from 'app/core/services/consent.service';
import { AuthStateService } from 'app/core/services/auth-state.service';

import * as shajs from 'sha.js';
import { pageLocationOf } from 'app/utils/analytics-location.utils';
import { isInternalUser } from 'app/utils/internal-user.utils';
declare var gtag: Function;

@Injectable()
export class AnalyticsService {
  private readonly router = inject(Router);
  private readonly consentService = inject(ConsentService);
  private readonly authState = inject(AuthStateService);

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.sendPageView(event.urlAfterRedirects);
      });
  }

  /**
   * Sends an event for the signed-in user, identified only by the salted hash
   * of the e-mail (see `saltAndHash`).
   *
   * @remarks
   * Never pass the e-mail in the name or category: those are sent in clear.
   *
   * TODO(LGPD): until this fix every event carried the hash of `undefined`;
   * now each one carries the user's hash, as the login event already did.
   * List this pseudonymous id sent to Google Analytics (consent basis) in the
   * RIPD.
   */
  public trackEvent(eventName: string, eventCategory: string): void {
    if (!this.canTrack()) {
      return;
    }
    if (typeof gtag !== 'undefined') {
      const params: Record<string, string> = {
        event_category: eventCategory,
        event_label: eventName,
      };
      const userId = this.authState.getUser()?.id;
      if (userId) {
        params.userId = this.saltAndHash(userId);
      }
      gtag('event', eventName, params);
    }
  }

  /**
   * @param userId - The user the data is about, for events sent before the
   * session is saved (the login); the signed-in user otherwise.
   */
  public pushToDataLayer(data: unknown, userId?: string): void {
    if (!this.canTrack(userId)) {
      return;
    }
    const w: any = window;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push(data);
  }

  /**
   * @remarks
   * Only the route path is reported (see `pageLocationOf`), and it is also set
   * as the location of every later hit, events included, which gtag would
   * otherwise fill with the full document URL.
   */
  public sendPageView(url: string): void {
    if (!this.canTrack()) {
      return;
    }
    if (typeof gtag !== 'undefined') {
      const pageLocation = pageLocationOf(url);
      gtag('set', { page_location: pageLocation });
      gtag('event', 'page_view', {
        page_location: pageLocation,
        page_title: document.title,
      });
    }
  }

  public sendEvent(eventName: string, params: object): void {
    if (!this.canTrack()) {
      return;
    }
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, params);
    }
  }

  public submitUserLoginEvent(user: any): void {
    this.pushToDataLayer(
      {
        event: 'userLogin',
        userId: this.saltAndHash(user.id),
      },
      user.id,
    );
  }

  public submitRegistrationStartEvent(): void {
    this.pushToDataLayer({ event: 'registrationStart' });
  }

  public submitRegistrationCompleteEvent(user: any): void {
    this.pushToDataLayer(
      {
        event: 'registrationComplete',
        userId: this.saltAndHash(user.id),
      },
      user.id,
    );
  }

  public submitRegistrationFailureEvent(user: any, errorMessage: string): void {
    this.pushToDataLayer(
      {
        event: 'registrationFailure',
        userId: this.saltAndHash(user.id),
        language: user.lang,
        errorMessage,
      },
      user.id,
    );
  }

  public submitFileUploadLimitEvent(amountReached: number): void {
    this.pushToDataLayer({
      event: 'fileUploadLimit',
      maxFileUploadsAllowed: amountReached,
    });
  }

  public submitFileUploadAttempt(nomeMd5: string): void {
    this.pushToDataLayer({ event: 'fileUploadAttempt', fileId: nomeMd5 });
  }

  public submitFileOpenEvent(nomeArquivo: string): void {
    this.pushToDataLayer({ event: 'fileOpen', fileId: nomeArquivo });
  }

  public submitFileDeleteEvent(
    fileId: string,
    tierUploadQuota: TierUploadQuota,
  ): void {
    const { maxFileUploadsAllowed, remainingFileUploads } = tierUploadQuota;
    this.pushToDataLayer({
      event: 'fileDelete',
      fileId,
      maxFileUploadsAllowed,
      remainingFileUploads,
    });
  }

  /**
   * @param userId - The user the hit is about; the signed-in user when absent.
   *
   * @remarks
   * Nothing is sent without analytics consent, nor for staff accounts
   * (`isInternalUser`), so that internal use is not counted as customer usage.
   * The only other hit is the page view of the GA `config` call, which
   * `ConsentService` turns off for staff in the same way.
   */
  private canTrack(userId?: string): boolean {
    if (!this.consentService.hasAnalyticsConsent()) {
      return false;
    }
    return !isInternalUser(userId ?? this.authState.getUser()?.id);
  }

  public saltAndHash(str: string): string {
    return shajs('sha256').update(`#[v1nEg4r-]${str}%buB1e$s`).digest('base64');
  }
}
